import type {
  EscolherWorkspaceInicialOptions,
  ResolveWorkspaceIdOptions,
} from '@/shared/types/workspace';

const WORKSPACE_STORAGE_KEY = 'currentEscritorioId';
const workspaceChangedEvent = 'workspaceChanged';

export const subscribeStoredWorkspaceId = (onChange: () => void): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  const handleStorage = (event: StorageEvent) => {
    if (
      event.storageArea === window.localStorage &&
      (event.key === WORKSPACE_STORAGE_KEY || event.key === null)
    )
      onChange();
  };
  window.addEventListener(workspaceChangedEvent, onChange);
  window.addEventListener('storage', handleStorage);
  return () => {
    window.removeEventListener(workspaceChangedEvent, onChange);
    window.removeEventListener('storage', handleStorage);
  };
};

export const getStoredWorkspaceId = (): string | null => {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(WORKSPACE_STORAGE_KEY);
};

export const setStoredWorkspaceId = (workspaceId: string): void => {
  if (typeof window === 'undefined') return;
  const normalizedWorkspaceId = workspaceId.trim();
  if (!normalizedWorkspaceId) {
    removeStoredWorkspaceId();
    return;
  }

  window.localStorage.setItem(WORKSPACE_STORAGE_KEY, normalizedWorkspaceId);
  // O evento nativo storage só notifica outras abas; os consumidores locais precisam reagir também.
  window.dispatchEvent(new Event(workspaceChangedEvent));
};

export const removeStoredWorkspaceId = (): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(WORKSPACE_STORAGE_KEY);
  window.dispatchEvent(new Event(workspaceChangedEvent));
};

/** Chave única do estado de impersonação persistido (fonte para todos os consumidores). */
export const IMPERSONATION_STORAGE_KEY = 'admin_impersonation';

/**
 * Tenant da impersonação ATIVA, ou null. A presença da chave sozinha não
 * basta: um estado antigo que nunca foi encerrado bypassaria a validação de
 * workspace para sempre — só vale com `isImpersonating` e um tenant definido.
 */
export const getImpersonatedTenantId = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!raw) return null;
    const state: unknown = JSON.parse(raw);
    if (typeof state !== 'object' || state === null) return null;
    const { isImpersonating, targetTenantId } = state as Record<string, unknown>;
    return isImpersonating === true && typeof targetTenantId === 'string' ? targetTenantId : null;
  } catch {
    return null;
  }
};

/**
 * Decide o workspace inicial da sessão. O id do localStorage é compartilhado
 * entre CONTAS no mesmo navegador e o `GET /escritorios/:id` é escopado por
 * empresa (não por vínculo de membro) — um resíduo de sessão anterior
 * carregava um workspace do qual o usuário logado nem participa, e o
 * recém-cadastrado do self abria fora do próprio escritório. Um id guardado só
 * é honrado se está na lista acessível; exceção: impersonação ativa, em que o
 * admin abre workspace alheio de propósito. `idsAcessiveis === null`
 * (listagem indisponível) mantém o comportamento permissivo antigo.
 */
export const escolherWorkspaceInicial = (
  options: EscolherWorkspaceInicialOptions,
): string | null => {
  const { localStoredId, lastWorkspaceId, idsAcessiveis, tenantImpersonado } = options;
  const acessivel = (id: string | null | undefined): id is string =>
    !!id && (idsAcessiveis === null || idsAcessiveis.has(id));

  if (localStoredId && (localStoredId === tenantImpersonado || acessivel(localStoredId))) {
    return localStoredId;
  }
  return acessivel(lastWorkspaceId) ? lastWorkspaceId : null;
};

export const resolveWorkspaceId = (options: ResolveWorkspaceIdOptions): string | undefined => {
  const explicitWorkspaceId = options.explicitWorkspaceId?.trim();
  const storedWorkspaceId = getStoredWorkspaceId()?.trim() ?? undefined;
  const userWorkspaceId = options.user?.escritorioId?.trim();

  return explicitWorkspaceId || storedWorkspaceId || userWorkspaceId || undefined;
};
